<template>
  <v-text-field
    :value="value"
    :label="label"
    :error="!!errorMsgs.length"
    :error-messages="errorMsgs"
    persistent-placeholder
    :placeholder="$t('generic.enter_slug')"
    :disabled="!domain || !domain.length || props.disabled"
    :rules="rules"
    :hide-details="'auto'"
    :prefix="domainUrl ? `https://${domainUrl}` + '/' : ''"
    @input="handleInput">
    <template v-slot:label> {{ label }}<span v-if="required" class="required__icon">*</span> </template>
  </v-text-field>
</template>

<script>
  import { dataProvider } from "@/@core/http";

  import Input from "@inulti/vuetify-admin/src/mixins/input";
  import { ref, onBeforeMount, watch, computed, getCurrentInstance } from "@vue/composition-api";
  import debounce from "lodash.debounce";
  import themeConfig from "@themeConfig";
  import { sanitizeSlug } from "@/utility/sanitizeSlug";

  export default {
    name: "VaSlugChecker",
    mixins: [Input],
    props: {
      value: {
        type: String,
        default: "",
      },
      typeToCheck: {
        type: String,
        default: "pageSlug",
      },
      label: {
        type: String,
        required: true,
      },
      domain: {
        type: String,
        default: "",
      },
      domainUrl: {
        type: String,
        default: "",
      },
      slug: {
        type: String,
        default: "",
      },
      required: {
        type: Boolean,
        default: true,
      },
      rules: {
        type: Array,
        default() {
          let rules = [];

          if (this.required) {
            rules.push(v => !!v || this.$t("va.forms.required_field", { field: this.label }));
          }

          return rules;
        },
      },
      /**
       * When editing a page, on initial page load, we should skip the check for already existing slug.
       * That is because on initial load, the slug that will be loaded into slugChecker will inevitably exist as slug
       * of the page that is being edited.
       *
       * Also, when editing a page, user might change the slug and then revert the changes, which would in turn cause
       * additional slug check, which would display an error that slug already exists, even though slug exists only as part of the page
       * that is currently being edited. That's why when editForm is true, we have to check if slug matches the initially passed slug value.
       */
      editForm: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["updateSlug", "updateLoading"],
    setup(props, { emit }) {
      let errMessage = ref("");
      const initialSlugValue = props.value;
      const currentInstance = getCurrentInstance();

      const getId = idToParse => {
        return idToParse.replace("/api/domains/", "");
      };

      const errorMsgs = computed(() => {
        const errors = [];
        if (errMessage.value) errors.push(errMessage.value);

        return [...errors, ...currentInstance.proxy?.commonProps?.errorMessages];
      });

      const checkIfSlugExists = async (domainId, type, slug) => {
        const { data: response } = await dataProvider.customHttpCall(
          "GET",
          `domains/${domainId}/check_slug?${type}=${slug}`,
        );
        // eslint-disable-next-line no-prototype-builtins
        if (response.hasOwnProperty("message")) {
          return response.message.exists;
        } else {
          return response["hydra:title"];
        }
      };

      const checkSlug = debounce(async slug => {
        //TODO: in future resolve problem with i18n in composition api and export it to translations file
        /**
         * Check slug only if slug checker is used in create/duplicate form
         * OR
         * when slug checker is used in edit form and initially loaded slug value is not the same to entered slug.
         */
        if ((!props.editForm || (props.editForm && initialSlugValue !== slug.trim())) && props.domain) {
          // emitting input value to model before slug check to prevent race condition when domain is changed
          const response = await checkIfSlugExists(getId(props.domain), props.typeToCheck, slug);
          if (response === true) {
            errMessage.value = "This slug already exists in this site. Please choose a different slug";
          } else if (response === false) {
            // Success, no such slug exists.
            errMessage.value = null;
            currentInstance.proxy && (currentInstance.proxy.internalErrorMessages = []);
          } else {
            errMessage.value = response;
          }
        } else {
          errMessage.value = null;
        }

        emit("updateLoading", false);
      }, 300);

      onBeforeMount(async () => {
        if (!props.domain?.length) return;
        if (props.editForm) return;
        emit("updateLoading", true);
        await checkSlug(props.value);
      });

      watch(
        () => props.domain,
        async () => {
          emit("updateLoading", true);
          await checkSlug(props.value);
        },
      );

      const handleInput = async slugValue => {
        emit("updateLoading", true);
        emit("input", sanitizeSlug(slugValue));
        await checkSlug(slugValue);
      };

      return {
        props,
        checkSlug,
        handleInput,
        errMessage,
        themeConfig,
        errorMsgs,
      };
    },
  };
</script>
